// packages/storefront/src/api/contactRequestProtection.ts
// Browser-only, plain fetch: the generated site's small contact island must not pull in axios.
import type { ContactRequestChallenge, ContactRequestProtection } from '@tourtask/types/contactRequest';
import { apiV1BaseUrl } from './baseUrls';

export interface ContactRequestProofClient {
  prepare(): Promise<void>;
  take(companyWebsite: string): Promise<ContactRequestProtection | null>;
}

export function createContactRequestProofClient(bizIdOrSlug: string, bizToken: string): ContactRequestProofClient {
  let pending: Promise<ContactRequestChallenge | null> | null = null;
  let receivedAt = 0;

  const load = async (): Promise<ContactRequestChallenge | null> => {
    try {
      const response = await fetch(`${apiV1BaseUrl}/businesses/${encodeURIComponent(bizIdOrSlug)}/contact-requests/challenge`, {
        method: 'POST', headers: { Authorization: `Bearer ${bizToken}` }, credentials: 'omit',
        cache: 'no-store', signal: AbortSignal.timeout(15_000),
      });
      if (!response.ok) return null;
      const result: unknown = await response.json();
      if (typeof result !== 'object' || result === null || !('success' in result) || result.success !== true || !('data' in result)) return null;
      const data = result.data;
      if (typeof data !== 'object' || data === null || !('token' in data) || typeof data.token !== 'string'
        || !('minFillMs' in data) || typeof data.minFillMs !== 'number'
        || !('expiresInMs' in data) || typeof data.expiresInMs !== 'number') return null;
      receivedAt = Date.now();
      return { token: data.token, minFillMs: data.minFillMs, expiresInMs: data.expiresInMs };
    } catch {
      return null;
    }
  };
  const ensure = (): Promise<ContactRequestChallenge | null> => pending ??= load();
  return {
    async prepare() { await ensure(); },
    async take(companyWebsite) {
      let challenge = await ensure();
      if (challenge === null) { pending = null; return null; }
      if (Date.now() - receivedAt >= challenge.expiresInMs - 5_000) {
        pending = null;
        challenge = await ensure();
      }
      if (challenge === null) { pending = null; return null; }
      const wait = Math.max(0, challenge.minFillMs - (Date.now() - receivedAt));
      if (wait > 0) await new Promise<void>((resolve) => setTimeout(resolve, wait));
      // A POST may have succeeded even if its answer was lost. Never retry with the same token.
      pending = null;
      return { token: challenge.token, companyWebsite };
    },
  };
}
